import React, { Suspense, useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { ArrowRight } from 'react-feather';
import { useNavigate } from 'react-router';
import { ProjectModeSelector, type ProjectModeOption } from '../components/project-mode-selector';
import { type ProjectType, type ContextIntegrationProvider } from '@/api-types';
import { useAuth } from '@/contexts/auth-context';
import { useFeature } from '@/features';
import { useCreateApp } from '@/hooks/use-create-app';
import { usePaginatedApps } from '@/hooks/use-paginated-apps';
import { AnimatePresence, LayoutGroup, motion } from 'framer-motion';
import { AppCard } from '@/components/shared/AppCard';
import clsx from 'clsx';
import { useAttachmentUpload, ALL_ACCEPTED_MIME_TYPES } from '@/hooks/use-attachment-upload';
import { useDragDrop } from '@/hooks/use-drag-drop';
import { AttachmentUploadButton } from '@/components/attachment-upload-button';
import { AttachmentPreview } from '@/components/attachment-preview';
import { UrlAttachmentInput } from '@/components/url-attachment-input';
import { toast } from 'sonner';
import { useVoiceInput } from '@/hooks/use-voice-input';
import { VoiceInputButton } from '@/components/VoiceInputButton';

const AnonymousLandingSections = React.lazy(
    () => import('../components/landing/AnonymousLandingSections'),
);

const SUGGESTION_PROMPTS = [
	'A task management app with drag-and-drop kanban boards',
	'Landing page for a SaaS product with pricing section',
	'Personal finance tracker with charts and budgeting',
	'Recipe sharing app with search and categories',
];

export default function Home() {
	const navigate = useNavigate();
	const createApp = useCreateApp();
	const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const [projectMode, setProjectMode] = useState<ProjectType>('app');
	const [query, setQuery] = useState('');
	const { isLoadingCapabilities, capabilities, getEnabledFeatures } = useFeature();

	const modeOptions = useMemo<ProjectModeOption[]>(() => {
		if (isLoadingCapabilities || !capabilities) return [];
		return getEnabledFeatures().map((def) => ({
			id: def.id,
			label:
				def.id === 'presentation'
					? 'Slides'
					: def.id === 'general'
						? 'General'
						: 'App',
			description: def.description,
		}));
	}, [capabilities, getEnabledFeatures, isLoadingCapabilities]);

	const showModeSelector = modeOptions.length > 1;

	useEffect(() => {
		if (isLoadingCapabilities) return;
		if (modeOptions.length === 0) {
			if (projectMode !== 'app') setProjectMode('app');
			return;
		}
		if (!modeOptions.some((m) => m.id === projectMode)) {
			setProjectMode(modeOptions[0].id);
		}
	}, [isLoadingCapabilities, modeOptions, projectMode]);

	const { attachments, addFiles, addUrlAttachment, removeAttachment, clearAttachments, isProcessing } = useAttachmentUpload({
		onError: (error) => {
			console.error('Attachment upload error:', error);
			toast.error(error);
		},
	});
	const [showUrlInput, setShowUrlInput] = useState(false);

	const handleIntegrationClick = useCallback((_provider: ContextIntegrationProvider) => {
		toast.info('Connect integrations from your organization settings to browse content.');
	}, []);

	const { isDragging, dragHandlers } = useDragDrop({
		onFilesDropped: addFiles,
		accept: [...ALL_ACCEPTED_MIME_TYPES],
	});

	const [supportsVoiceInput, setSupportsVoiceInput] = useState(false);
	useEffect(() => {
		setSupportsVoiceInput(typeof navigator !== 'undefined' && !!navigator.mediaDevices);
	}, []);

	const queryRef = useRef(query);
	queryRef.current = query;

	const handleCreateApp = useCallback(
		(query: string, mode: ProjectType) => {
			createApp(query, mode, {
				attachments,
				onComplete: clearAttachments,
			});
		},
		[attachments, clearAttachments, createApp],
	);

	const handleCreateAppRef = useRef(handleCreateApp);
	handleCreateAppRef.current = handleCreateApp;
	const projectModeRef = useRef(projectMode);
	projectModeRef.current = projectMode;

	const { isRecording, isTranscribing, startRecording, stopRecording, error: voiceError, audioLevel, recordingDuration } = useVoiceInput({
		onTranscription: (text) => {
			const current = queryRef.current;
			const separator = current.trim() ? ' ' : '';
			const updated = current + separator + text;
			setQuery(updated);
			requestAnimationFrame(adjustTextareaHeight);
			handleCreateAppRef.current(updated, projectModeRef.current);
		},
	});

	const placeholderPhrases = useMemo(() => [
		"todo list app",
		"F1 fantasy game",
		"personal finance tracker"
	], []);
	const [currentPlaceholderPhraseIndex, setCurrentPlaceholderPhraseIndex] = useState(0);
	const [currentPlaceholderText, setCurrentPlaceholderText] = useState("");
	const [isPlaceholderTyping, setIsPlaceholderTyping] = useState(true);

	const {
		apps,
		loading,
	} = usePaginatedApps({
		type: 'public',
		defaultSort: 'popular',
		defaultPeriod: 'week',
		limit: 6,
	});

	const discoverReady = useMemo(() => !loading && (apps?.length ?? 0) > 5, [loading, apps]);

	const adjustTextareaHeight = useCallback(() => {
		if (textareaRef.current) {
			textareaRef.current.style.height = 'auto';
			const scrollHeight = textareaRef.current.scrollHeight;
			const maxHeight = 300;
			textareaRef.current.style.height =
				Math.min(scrollHeight, maxHeight) + 'px';
		}
	}, []);

	useEffect(() => {
		adjustTextareaHeight();
	}, [adjustTextareaHeight]);

	useEffect(() => {
		const currentPhrase = placeholderPhrases[currentPlaceholderPhraseIndex];

		if (isPlaceholderTyping) {
			if (currentPlaceholderText.length < currentPhrase.length) {
				const timeout = setTimeout(() => {
					setCurrentPlaceholderText(currentPhrase.slice(0, currentPlaceholderText.length + 1));
				}, 100);
				return () => clearTimeout(timeout);
			} else {
				const timeout = setTimeout(() => {
					setIsPlaceholderTyping(false);
				}, 2000);
				return () => clearTimeout(timeout);
			}
		} else {
			if (currentPlaceholderText.length > 0) {
				const timeout = setTimeout(() => {
					setCurrentPlaceholderText(currentPlaceholderText.slice(0, -1));
				}, 50);
				return () => clearTimeout(timeout);
			} else {
				setCurrentPlaceholderPhraseIndex((prev) => (prev + 1) % placeholderPhrases.length);
				setIsPlaceholderTyping(true);
			}
		}
	}, [currentPlaceholderText, currentPlaceholderPhraseIndex, isPlaceholderTyping, placeholderPhrases]);

	const handleSuggestionClick = (prompt: string) => {
		setQuery(prompt);
		if (textareaRef.current) {
			textareaRef.current.focus();
		}
		requestAnimationFrame(adjustTextareaHeight);
	};

	if (isAuthLoading) {
		return <div className="w-full min-h-screen" aria-hidden />;
	}

	if (!isAuthenticated) {
		return (
			<Suspense fallback={<div className="w-full min-h-screen" aria-hidden />}>
				<AnonymousLandingSections />
			</Suspense>
		);
	}

	return (
		<div className="relative flex flex-col items-center size-full">
			<div className="fixed inset-0 text-accent z-0 opacity-10 pointer-events-none">
				<svg width="100%" height="100%">
					<defs>
						<pattern
							id=":S2:"
							viewBox="-6 -6 12 12"
							patternUnits="userSpaceOnUse"
							width="12"
							height="12"
						>
							<circle
								cx="0"
								cy="0"
								r="1"
								fill="currentColor"
							></circle>
						</pattern>
					</defs>
					<rect
						width="100%"
						height="100%"
						fill="url(#:S2:)"
					></rect>
				</svg>
			</div>

			<LayoutGroup>
				<div className="w-full max-w-3xl px-6">
					<motion.div
						layout
						transition={{ layout: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } }}
						className={clsx(
							"flex flex-col items-center z-10",
							discoverReady ? "pt-32 sm:pt-36" : "pt-[22vh] sm:pt-[26vh]"
						)}>
						<h1 className="text-6xl sm:text-7xl font-bold leading-[1.05] tracking-tight text-center mb-4 bg-gradient-to-b from-text-primary to-text-primary/50 bg-clip-text text-transparent">
							Build any app
						</h1>
						<p className="text-text-tertiary text-lg sm:text-xl text-center mb-10">
							Describe your idea and we'll build it for you.
						</p>

						<form
							id="composer"
							method="POST"
							onSubmit={(e) => {
								e.preventDefault();
								const query = textareaRef.current!.value;
								handleCreateApp(query, projectMode);
							}}
							className="flex z-10 flex-col w-full min-h-[140px] bg-bg-4 border border-accent/20 dark:border-accent/40 dark:bg-bg-2 rounded-2xl shadow-lg p-6 transition-all duration-200 focus-within:shadow-xl focus-within:border-accent/40 dark:focus-within:border-accent/60"
						>
							<div
								className={clsx(
									"flex-1 flex flex-col relative",
									isDragging && "ring-2 ring-accent ring-offset-2 rounded-lg"
								)}
								{...dragHandlers}
							>
								{isDragging && (
									<div className="absolute inset-0 flex items-center justify-center bg-accent/10 backdrop-blur-sm rounded-lg z-30 pointer-events-none">
										<p className="text-accent font-medium">Drop files here</p>
									</div>
								)}
								<textarea
									className="w-full resize-none ring-0 z-20 outline-0 placeholder:text-text-primary/40 text-text-primary text-lg"
									name="query"
									value={query}
									placeholder={`Create a ${currentPlaceholderText}`}
									ref={textareaRef}
									onChange={(e) => {
										setQuery(e.target.value);
										adjustTextareaHeight();
									}}
									onInput={adjustTextareaHeight}
									onKeyDown={(e) => {
										if (e.key === 'Enter' && !e.shiftKey) {
											e.preventDefault();
											const query = textareaRef.current!.value;
											handleCreateApp(query, projectMode);
										}
									}}
								/>
								{attachments.length > 0 && (
									<div className="mt-3">
										<AttachmentPreview
											attachments={attachments}
											onRemove={removeAttachment}
										/>
									</div>
								)}
								{showUrlInput && (
									<div className="mt-2">
										<UrlAttachmentInput
											onUrlFetched={addUrlAttachment}
											onClose={() => setShowUrlInput(false)}
										/>
									</div>
								)}
							</div>
							<div
								className={clsx(
									'flex items-center mt-4 pt-2',
									showModeSelector ? 'justify-between' : 'justify-between',
								)}
							>
								{showModeSelector && (
									<ProjectModeSelector
										value={projectMode}
										onChange={setProjectMode}
										modes={modeOptions}
										className="flex-1"
									/>
								)}

								<div className="flex items-center gap-1">
									<AttachmentUploadButton
										onFilesSelected={addFiles}
										onUrlClick={() => setShowUrlInput(!showUrlInput)}
										onIntegrationClick={handleIntegrationClick}
										availableProviders={capabilities?.integrations?.contextProviders}
										disabled={isProcessing}
										compact
									/>
									{supportsVoiceInput && (
										<VoiceInputButton
											isRecording={isRecording}
											isTranscribing={isTranscribing}
											onToggle={isRecording ? stopRecording : startRecording}
											disabled={isProcessing}
											error={voiceError}
											audioLevel={audioLevel}
											recordingDuration={recordingDuration}
										/>
									)}
								</div>

								<div className={clsx('flex items-center gap-2', showModeSelector && 'ml-4')}>
									<button
										type="submit"
										disabled={!query.trim()}
										className="bg-accent text-white p-2 rounded-lg *:size-5 transition-all duration-200 hover:shadow-md hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed"
									>
										<ArrowRight />
									</button>
								</div>
							</div>
						</form>

						<div className="w-full mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
							{SUGGESTION_PROMPTS.map((prompt) => (
								<button
									key={prompt}
									type="button"
									onClick={() => handleSuggestionClick(prompt)}
									className="text-left px-4 py-3 text-sm text-text-secondary/80 bg-bg-4/50 dark:bg-bg-2/40 border border-text-tertiary/15 rounded-xl transition-all duration-150 hover:border-accent/30 hover:bg-bg-4 dark:hover:bg-bg-2/70 hover:text-text-secondary"
								>
									{prompt}
								</button>
							))}
						</div>
					</motion.div>
				</div>

				<AnimatePresence>
					{discoverReady && (
						<motion.section
							key="discover-section"
							layout
							initial={{ opacity: 0, height: 0 }}
							animate={{ opacity: 1, height: "auto" }}
							exit={{ opacity: 0, height: 0 }}
							transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
							className="max-w-6xl mx-auto px-4 z-10 mt-24 mb-8"
						>
							<div className='flex flex-col items-start'>
								<h2 className="text-2xl font-medium text-text-secondary/80">Discover</h2>
								<div className="text-md font-light mb-4 text-text-tertiary hover:underline underline-offset-4 select-text cursor-pointer" onClick={() => navigate('/discover')}>View All</div>
								<motion.div
									layout
									transition={{ duration: 0.4 }}
									className="grid grid-cols-2 xl:grid-cols-3 gap-6"
								>
									<AnimatePresence mode="popLayout">
										{apps.map(app => (
											<AppCard
												key={app.id}
												app={app}
												onClick={() => navigate(`/app/${app.id}`)}
												showStats={true}
												showUser={true}
												showActions={false}
											/>
										))}
									</AnimatePresence>
								</motion.div>
							</div>
						</motion.section>
					)}
				</AnimatePresence>
			</LayoutGroup>
		</div>
	);
}
